import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Observable, catchError, throwError } from 'rxjs';

import {
  LocalStorageService,
  SSTColorVariants,
  SSTConfirmationDialogComponent,
  SSTSnackbarComponent,
  SignalStore,
  parseHttpErrorMessage
} from 'bcte-common-lib';

export type AppThemes = 'light' | 'dark';

export interface AppState {
  isSidenavOpen: boolean;
  theme: AppThemes;
}

@Injectable({ providedIn: 'root' })
export class AppService {

  private dialog = inject(MatDialog);
  private snackbar = inject(MatSnackBar);
  private localStorage = inject(LocalStorageService);

  /** Application state store */
  private appStore: SignalStore<AppState> = new SignalStore(
    { name: 'app' },
    { isReady: false, isSidenavOpen: true, theme: (this.localStorage.getItem('theme') as AppThemes) || 'dark' }
  );

  /** Selectors */
  public isSidenavOpen$ = this.appStore.select(state => state.isSidenavOpen);
  public theme$ = this.appStore.select(state => state.theme);

  constructor() {}

  /**
   * Show a confirmation dialog
   * @param title title of dialog
   * @param message Message body of dialog
   */
  public confirm(title: string, message: string): Observable<boolean> {
    const confirmDialog = this.dialog.open(SSTConfirmationDialogComponent, { data: { title, message } });

    return confirmDialog.afterClosed();
  }

  /**
   * Show/hide sidenav
   * @param isSidenavOpen whether to open or close side nav
   */
  public toggleSideNav(isSidenavOpen: boolean): void {
    this.appStore.update({ isSidenavOpen });
  }

  /**
   * Change app theme
   * @param theme new theme
   */
  public changeTheme(theme: AppThemes): void {
    this.appStore.update({ theme });
    this.localStorage.setItem('theme', theme);
    this.applyTheme();
  }

  /**
   * Update theme in document
   */
  public applyTheme(): void {
    const previousTheme = this.theme$() === 'light' ? 'dark' : 'light';
    const classList = document.body.classList;

    // Remove old theme
    if (classList.contains(previousTheme)) {
      classList.remove(previousTheme);
    }

    // Add new theme
    document.body.classList.add(this.theme$());
  }

  /**
   * Show loading indicator
   * @param message Loading message
   */
  public showLoader(message: string = 'Loading...') {
    console.log(message);
  }

  public displayError(message: string) {
    return this.displayNotification('warn', message, 5000);
  }

  /**
   * Pipeable operator to show the loading screen overlay
   * @param {string} text Loading screen text
   */
  public handleError(
    text = 'Error processing request',
    callback?: (err: unknown) => void
  ): <T>(source: Observable<T>) => Observable<T> {
    return <T>(source: Observable<T>) => {
      return source.pipe(
        catchError(e => {
          this.displayError(parseHttpErrorMessage(e, text));

          if (callback) {
            callback(e);
          }

          return throwError(() => e);
        })
      );
    };
  }

  /**
   * Display notification snackbar
   * @param type color variant
   * @param message message to display
   * @param duration the duration in ms todisplay, default to 3000
   * @param cancel true to cancel
   */
  public displayNotification(
    type: SSTColorVariants,
    message: string,
    duration = 3000,
    cancel = false
  ): MatSnackBarRef<SSTSnackbarComponent> {
    return this.snackbar.openFromComponent<SSTSnackbarComponent>(SSTSnackbarComponent, {
      duration,
      data: { type, message, cancel }
    });
  }

  formatDateToLocaleDateString(e: Date): string {
    return (e.toLocaleDateString('en-US', { dateStyle: 'short' }));
  }

  public checkValidNumericId(id: number|undefined) : number
  {
    if(id === undefined)
      throw new Error("Undefined ID");
    else
      return id;
  }
}
